// Gratisnivån: full funktionalitet under en provperiod, med kvoter och spärrar mot missbruk.
import { supabase } from "@/integrations/supabase/client";

/** Meddelande när en kvot på provperioden är slut. */
export const FREE_QUOTA_MESSAGE = {
  ai: "AI-kvoten för provperioden är slut (50 anrop). Uppgradera för obegränsat AI-stöd.",
  link: "Provperiodens 10 externa länkar är använda. Uppgradera för fler.",
  export: "Provperiodens 20 Word-exporter är använda. Uppgradera för fler.",
} as const;


/* Databastyperna genereras om först vid nästa typ-synk – därför lokala typer här. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const rpc = (name: string, args?: Record<string, unknown>) => (supabase.rpc as any)(name, args);

export interface FreeTierState {
  is_free: boolean;
  expires_at: string | null;
  days_left: number;
  expired: boolean;
  ai_used: number;
  ai_limit: number;
  links_used: number;
  links_limit: number;
  exports_used: number;
  exports_limit: number;
  project_limit: number;
  projects_used: number;
}

export type QuotaKind = "ai" | "link" | "export";

export const QUOTA_LABEL: Record<QuotaKind, string> = {
  ai: "AI-anrop",
  link: "externa länkar",
  export: "Word-exporter",
};

export const CLAIM_REASON_TEXT: Record<string, string> = {
  org_already_used: "Organisationsnumret har redan använt sin gratisperiod.",
  domain_already_used: "Ett konto på samma företagsdomän har redan använt gratisperioden.",
  invalid_org_no: "Ange ett giltigt organisationsnummer (10 siffror).",
  disposable_email: "Engångsadresser kan inte användas för gratisperioden.",
  not_authenticated: "Du behöver vara inloggad.",
};

/** Enkel, stabil enhetssignal – hashad, inga personuppgifter sparas. */
export const deviceFingerprint = async (): Promise<string | null> => {
  if (typeof window === "undefined") return null;
  try {
    const raw = [
      navigator.userAgent,
      navigator.language,
      String(screen.width),
      String(screen.height),
      String(screen.colorDepth),
      String(new Date().getTimezoneOffset()),
      Intl.DateTimeFormat().resolvedOptions().timeZone ?? "",
    ].join("|");
    const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(raw));
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  } catch {
    return null;
  }
};

/* Cache så att dokumentgeneratorer (som är synkrona) vet om provperioden gäller. */
let trialWatermark = false;
export const isTrialWatermarkActive = (): boolean => trialWatermark;
export const setTrialWatermark = (active: boolean): void => {
  trialWatermark = active;
};

/**
 * Väntar in att inloggningssessionen är etablerad. Utan den skickas anropet
 * som anon-roll och databasen svarar "permission denied".
 */
const hasSession = async (): Promise<boolean> => {
  try {
    const { data } = await supabase.auth.getSession();
    return Boolean(data.session?.user?.id);
  } catch {
    return false;
  }
};

export const fetchFreeTierState = async (): Promise<FreeTierState | null> => {
  try {
    if (!(await hasSession())) return null;
    const { data, error } = await rpc("get_free_tier_state");
    if (error) return null;
    const row = ((data as FreeTierState[] | null) ?? [])[0] ?? null;
    setTrialWatermark(Boolean(row?.is_free));
    return row;
  } catch {
    return null;
  }
};

export const claimFreeTier = async (
  orgNo: string,
  emailDomain: string
): Promise<{ granted: boolean; reason: string; expiresAt: string | null }> => {
  const fingerprint = await deviceFingerprint();
  const { data, error } = await rpc("claim_free_tier", {
    p_org_no: orgNo || null,
    p_email_domain: emailDomain || null,
    p_fingerprint: fingerprint,
  });
  if (error) {
    return { granted: false, reason: "unknown", expiresAt: null };
  }
  const row = ((data as { granted: boolean; reason: string; expires_at: string | null }[] | null) ??
    [])[0];
  return {
    granted: Boolean(row?.granted),
    reason: row?.reason ?? "unknown",
    expiresAt: row?.expires_at ?? null,
  };
};

/**
 * Räknar av en kvot på gratisnivån. Betalande konton släpps alltid igenom,
 * liksom nätverksfel – kvoten är ett kostnadsskydd, inte en säkerhetsspärr.
 */
export const consumeFreeQuota = async (
  kind: QuotaKind
): Promise<{ allowed: boolean; used: number; quota: number }> => {
  try {
    const { data, error } = await rpc("consume_free_quota", { p_kind: kind });
    if (error) return { allowed: true, used: 0, quota: 0 };
    const row = ((data as { allowed: boolean; used: number; quota: number }[] | null) ?? [])[0];
    if (row && row.quota > 0) setTrialWatermark(true);
    return row ?? { allowed: true, used: 0, quota: 0 };
  } catch {
    return { allowed: true, used: 0, quota: 0 };
  }
};

export interface FreeTierAdminRow {
  user_id: string;
  org_no: string | null;
  email_domain: string | null;
  source: string | null;
  expires_at: string | null;
  expired: boolean;
  ai_used: number;
  links_used: number;
  exports_used: number;
  same_domain_accounts: number;
  same_signal_accounts: number;
  denied_attempts: number;
}

export const fetchFreeTierAccounts = async (): Promise<FreeTierAdminRow[]> => {
  const { data, error } = await rpc("admin_list_free_tier_accounts");
  if (error) return [];
  return (data as FreeTierAdminRow[] | null) ?? [];
};

export const extendFreeTier = async (userId: string, days: number): Promise<boolean> => {
  const { error } = await rpc("admin_extend_free_tier", { p_user_id: userId, p_days: days });
  return !error;
};
